/**
 * The token contract between the console and the API.
 *
 * This file exists because the two ends disagreed in production: the console
 * sent the ID token while the API verified with `tokenUse: 'id'`... and then,
 * once corrected the other way, could have disagreed again. Comments in two
 * source files are not a contract — a shared constant that both sides *derive*
 * their behaviour from is.
 *
 * Do not read this value and branch on it in a third place. Call
 * `apiBearerToken` on the client and pass `API_TOKEN_USE` to the verifier on the
 * server; those are the only two consumers.
 */

/**
 * The access token, not the ID token.
 *
 * This is the standard OAuth 2.0 division of labour and the one we keep:
 *
 * - **access token** — authorizes calls to a resource server. Audience is the
 *   API. This is what goes in `Authorization: Bearer`.
 * - **ID token** — tells a *client* who signed in. Audience is the client, not
 *   the API. Never a credential for a resource server.
 * - **refresh token** — renews the other two, and never leaves the client.
 *
 * Using the ID token as an API bearer is a common shortcut, because it carries
 * `email` and therefore makes naive user lookup trivial. It is still wrong: the
 * API is not the token's audience, and the shortcut quietly makes a mutable
 * email claim load-bearing for identity. Identity here is the immutable `sub`
 * (ADR-012), so the API needs no email claim at all to know who is calling.
 */
export const API_TOKEN_USE = 'access' as const;

export type ApiTokenUse = typeof API_TOKEN_USE;

/** The two tokens a client holds after an authorization code exchange. */
export interface OAuthTokenPair {
  readonly idToken: string;
  readonly accessToken: string;
}

/**
 * Selects the token that belongs in `Authorization: Bearer` for this API.
 *
 * The single source of truth for the client half of the contract. Written as a
 * function rather than a documented convention so that a test can hand it a
 * real signed pair and assert the API accepts exactly what it returns.
 */
export function apiBearerToken(tokens: OAuthTokenPair): string {
  return API_TOKEN_USE === 'access' ? tokens.accessToken : tokens.idToken;
}
